import { z } from 'zod';
import { Id, TimestampMs } from './common.js';
/**
 * `progress` — ground-truth evidence about a run's work, produced by the daemon's progress
 * probe (apps/daemon/src/progress), never by the agent. Additive v0 event kind.
 *
 * The probe runs after each turn (and on a timer while the run is busy). It inspects the run's
 * worktree with git and classifies the agent's shell tool calls that match the project's
 * landing recipe / check patterns, using their real exit codes. Optionally it runs cheap
 * checks itself (`progress.checks` in viko.project.yaml).
 *
 * Everything in this payload is **verified**: it comes from git state, process exit codes, or
 * commands the daemon ran. Clients mark anything derived only from the agent's own words
 * (todo lists, "all tests pass" in a reply) as **claimed**. See docs/protocol.md → Progress.
 */
export const CheckKind = z.enum(['test', 'typecheck', 'lint', 'build', 'custom']);
export const CheckStatus = z.enum(['pass', 'fail', 'error', 'running', 'skipped']);
export const ProgressCheck = z.object({
    /** Stable id: the agent's `toolCallId`, or `daemon:<name>:<n>` for daemon-run checks. */
    id: Id,
    kind: CheckKind,
    /** Short label (`test`, `typecheck`, or the configured check name). */
    name: z.string().optional(),
    command: z.string(),
    /** `agent`: an agent shell call the daemon classified; `daemon`: the daemon ran it. */
    source: z.enum(['agent', 'daemon']),
    toolCallId: z.string().optional(),
    status: CheckStatus,
    exitCode: z.number().int().optional(),
    /** Parsed from the tool output when the runner prints counts (vitest, jest, pytest, cargo, go). */
    passed: z.number().int().nonnegative().optional(),
    failed: z.number().int().nonnegative().optional(),
    skipped: z.number().int().nonnegative().optional(),
    total: z.number().int().nonnegative().optional(),
    /** Names of failing tests/files when the runner lists them (capped). */
    failing: z.array(z.string()).optional(),
    durationMs: z.number().nonnegative().optional(),
    /** When the check finished. */
    ts: TimestampMs,
    /** The landing-recipe command this check matched (`landing.test` / `landing.build`). */
    recipeStep: z.string().optional(),
    /** HEAD the check ran against, when known. */
    headSha: z.string().optional(),
    /** Check began and ended on this HEAD with no dirty files. */
    cleanTree: z.boolean().optional(),
    /**
     * Set when the check ran but its own exit status is unknown: `piped` (output filtered through
     * `| tail` etc., so the shell reported the filter's status) or `unreported` (the tool result
     * carried no exit code). `exitCode` is then absent and `status` only reflects parsed output
     * counts; clients show such results as activity, never as verification.
     */
    exitUnknown: z.enum(['piped', 'unreported']).optional(),
    /** `tasks`: the counts are turbo tasks (e.g. the tail of `pnpm verify`), not tests. */
    unit: z.enum(['tasks']).optional(),
});
export const ProgressCommit = z.object({
    sha: z.string(),
    subject: z.string(),
    ts: TimestampMs,
    files: z.number().int().nonnegative().optional(),
    additions: z.number().int().nonnegative().optional(),
    deletions: z.number().int().nonnegative().optional(),
});
export const ProgressGit = z.object({
    /**
     * Successful fetch time of the remote target ref, set only when `base` is at least as fresh
     * as that remote ref; absent for cached refs.
     */
    baseVerifiedAt: TimestampMs.optional(),
    branch: z.string().optional(),
    /** Ref the workstream lands onto: the local target branch (e.g. `main`), else `origin/main`. */
    base: z.string(),
    /** Tip of `base`. */
    baseSha: z.string().optional(),
    headSha: z.string().optional(),
    /**
     * The run's own base commit (its fork point; follows a rebase onto the target). `ahead`,
     * `commits` and `diffstat` count only work after it. Absent on older daemons.
     */
    forkSha: z.string().optional(),
    /** The run's own commits (`forkSha..HEAD`). */
    ahead: z.number().int().nonnegative(),
    /** Commits on `base` the run does not have (the target moved since the run's base). */
    behind: z.number().int().nonnegative(),
    /** The run's own commits, newest first (capped at 20). */
    commits: z.array(ProgressCommit),
    /**
     * The local target vs its remote ref (e.g. `main` vs `origin/main`). `ahead` = target commits
     * not on the remote (unpushed), `behind` = remote commits not on the target. Never run work.
     */
    remote: z
        .object({
        ref: z.string(),
        sha: z.string(),
        ahead: z.number().int().nonnegative(),
        behind: z.number().int().nonnegative(),
        verifiedAt: TimestampMs.optional(),
    })
        .optional(),
    /** `git diff --shortstat forkSha...HEAD`. */
    diffstat: z.object({
        files: z.number().int().nonnegative(),
        additions: z.number().int().nonnegative(),
        deletions: z.number().int().nonnegative(),
    }),
    /** Uncommitted work in the worktree. */
    dirty: z.object({
        files: z.number().int().nonnegative(),
        additions: z.number().int().nonnegative().optional(),
        deletions: z.number().int().nonnegative().optional(),
        paths: z.array(z.string()).optional(),
    }),
    /** Would HEAD merge onto `base` without conflicts (not proof of a clean rebase) (`git merge-tree`)? Absent = unknown. */
    mergesCleanly: z.boolean().optional(),
    conflicts: z.array(z.string()).optional(),
});
export const ProgressPayload = {
    probeId: Id,
    stallAfterMs: z.number().positive().optional(),
    trigger: z.enum(['turn.end', 'timer', 'check', 'manual']),
    /** Worktree state; absent when the run has no git worktree. */
    git: ProgressGit.optional(),
    /** Checks observed or run since the previous probe (not cumulative). */
    checks: z.array(ProgressCheck).default([]),
    /** The project's landing recipe, so clients can say "recipe green". */
    recipe: z.object({ test: z.array(z.string()), build: z.array(z.string()) }).optional(),
    /** The workstream landed (merged into base). */
    landed: z.object({ sha: z.string().optional(), ts: TimestampMs }).optional(),
};
//# sourceMappingURL=progress.js.map